import React, { useEffect, useRef, useState } from "react";

/**
 * useInView: ritorna [ref, isInView].
 * L'elemento viene rivelato una sola volta e poi rimane visibile.
 */
export const useInView = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    // Rispetta la preferenza utente per animazioni ridotte
    if (typeof window !== "undefined" &&
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }
    const el = ref.current;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      {
        threshold: options.threshold ?? 0.15,
        rootMargin: options.rootMargin ?? "0px 0px -50px 0px",
      }
    );
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, inView];
};

/**
 * Reveal: wrapper che rende visibile con fade + slide-up al primo scroll
 * dentro il viewport. Elegante e sobrio.
 *
 * Props:
 *  - delay: ms di ritardo (per staggering) — default 0
 *  - as: tag da renderizzare — default "div"
 *  - className: classi extra
 *  - y: pixel di slide-up iniziale — default 24
 */
const Reveal = ({
  children,
  delay = 0,
  as: Tag = "div",
  className = "",
  y = 24,
  ...rest
}) => {
  const [ref, inView] = useInView();
  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : `translateY(${y}px)`,
        transition: `opacity 700ms cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 700ms cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
        willChange: "opacity, transform",
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
